import React from 'react'
import { components } from 'react-select'
import { FormattedMessage } from 'react-intl'
import DropDownIndicatorClosedIcon24 from 'assets/icons/dropdown-indicator-closed24px'
import DropDownIndicatorClosedIcon16 from 'assets/icons/dropdown-indicator-closed16px'

export const tooltipXOffset = 0
export const tooltipYOffset = -16

export const getDropdownClosedIconBySize = (indicatorSize) => {
  if (indicatorSize === 'small') return <DropDownIndicatorClosedIcon16 />
  if (indicatorSize === 'none') return <div />
  return <DropDownIndicatorClosedIcon24 />
}

export const createDropdownIndicatorClosed = (indicatorSize) => {
  const DropdownIndicator = (props) => {
    const src = getDropdownClosedIconBySize(indicatorSize)
    return <components.DropdownIndicator {...props}>{src}</components.DropdownIndicator>
  }
  DropdownIndicator.displayName = `DropdownIndicator(${indicatorSize})`
  return DropdownIndicator
}

export const createPlaceholder = (placeholderId) => {
  return function Placeholder(props) {
    return (
      <components.Placeholder {...props}>
        <span style={{ paddingLeft: '8px', color: 'rgba(32, 32, 32, 0.7)', opacity: '0.7' }}>
          <FormattedMessage id={placeholderId} />
        </span>
      </components.Placeholder>
    )
  }
}

/**
 * Multi menu row: selected options show an X to remove (same as chip remove on the control).
 */
export function MultiSelectOptionWithRemove (props) {
  const { children, isSelected, isDisabled, data, selectProps } = props
  const { onChange, value, isMulti, getOptionValue } = selectProps
  const getId = (opt) => (typeof getOptionValue === 'function' ? getOptionValue(opt) : opt?.value)
  const optionId = getId(data)

  const onRemoveMouseDown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isMulti || !Array.isArray(value)) return
    const next = value.filter((v) => getId(v) !== optionId)
    onChange(next.length ? next : null, { action: 'remove-value', removedValue: data })
  }

  return (
    <components.Option {...props}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: '8px',
          minWidth: 0,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {children}
        </span>
        {isMulti && isSelected && !isDisabled && (
          <button
            type="button"
            className="multi-select-menu-remove"
            aria-label="Remove"
            onMouseDown={onRemoveMouseDown}
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#055a8c',
              fontSize: '18px',
              lineHeight: 1,
              padding: '0 4px',
              borderRadius: '4px',
            }}
          >
            ×
          </button>
        )}
      </div>
    </components.Option>
  )
}

export const SingleValueDefault = (props) => <components.SingleValue {...props} />

export const MenuListDefault = (props) => <components.MenuList className='scroll' {...props} />

export const getOptionHoverBackground = (state) => {
  if (state.isDisabled) return '#fff'
  if (state.isSelected) return 'rgba(10, 161, 244, 0.22)'
  return 'rgba(32, 32, 32, 0.03)'
}

const SELECT_BLUE_SOFT = 'rgba(10, 161, 244, 0.16)'
const SELECT_BLUE_SOFT_HOVER = 'rgba(10, 161, 244, 0.26)'

export const buildSelectStyles = ({
  error,
  field,
  height,
  menuWidth,
  inputMargin,
  paddingLeft,
  searchDropdown,
  twoStepWidth,
  visuallyDisabled = false,
  allowDisabledPointerStyle = true,
  includeExpandedMenu = false,
  isMulti = false,
  zIndex = 9999,
}) => {
  const selectHasValue = (state) =>
    typeof state.hasValue === 'boolean'
      ? state.hasValue
      : Array.isArray(state.selectProps?.value)
        ? state.selectProps.value.length > 0
        : Boolean(state.selectProps?.value)

  const errorBorderStyle = '1px solid rgba(225, 25, 0, 0.5)'
  const defaultBorderStyle = '1px solid rgba(32, 32, 32, 0.2)'
  const focusBorderStyle = '1px solid #0AA1F4'
  const defaultBoxShadow = '0px 1px 2px rgba(32, 32, 32, 0.05)'
  const focusBoxShadow = '0px 1px 2px rgba(32, 32, 32, 0.05), 0px 0px 0px 4px rgba(10, 161, 244, 0.1);'
  const defaultBorderColor = 'rgba(32, 32, 32, 0.2)'
  const focusBorderColor = '#0AA1F4'
  const singleValueWidth = twoStepWidth ? '250px' : '100%'
  const singleValueResponsiveWidth = searchDropdown ? '80%' : '100%'

  return {
    control: (provided, state) => {
      const isDisabledControl = state.isDisabled || visuallyDisabled
      let border = state.isFocused ? focusBorderStyle : defaultBorderStyle
      const boxShadow = state.isFocused ? focusBoxShadow : defaultBoxShadow
      if (error && error.field === field) border = errorBorderStyle

      return {
        ...provided,
        width: '100%',
        height,
        minHeight: height,
        marginBottom: '0px',
        alignItems: 'center',
        background: isDisabledControl ? 'rgba(32, 32, 32, 0.02)' : 'rgba(255, 255, 255, 1)',
        outline: 'transparent',
        borderColor: state.isFocused ? focusBorderColor : defaultBorderColor,
        borderWidth: '1px',
        border,
        boxShadow,
        paddingLeft,
        cursor: isDisabledControl && allowDisabledPointerStyle ? 'not-allowed' : 'pointer',
        '&:hover': {
          border: isDisabledControl && allowDisabledPointerStyle ? defaultBorderStyle : '1px solid rgba(32, 32, 32, 0.2)',
          boxShadow: isDisabledControl && allowDisabledPointerStyle ? defaultBoxShadow : '0px 2px 8px -2px rgba(32, 32, 32, 0.1)',
        },
      }
    },
    indicatorSeparator: () => ({ display: 'none' }),
    multiValue: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      maxWidth: '110px',
      flexShrink: 1,
      overflow: 'hidden',
      backgroundColor: SELECT_BLUE_SOFT,
      borderRadius: '4px',
      border: `1px solid rgba(10, 161, 244, 0.35)`,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#0a4d70',
      fontSize: '12px',
      padding: '0 2px 0 6px',
      maxWidth: '100%',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      cursor: 'pointer',
      borderRadius: '0 3px 3px 0',
      paddingLeft: '4px',
      paddingRight: '6px',
      color: '#055a8c',
      ':hover': {
        backgroundColor: 'rgba(10, 161, 244, 0.35)',
        color: '#003d5c',
      },
    }),
    menu: (provided) => ({
      ...provided,
      width: menuWidth,
      zIndex: 2,
      ...(includeExpandedMenu ? { maxHeight: 'none', overflow: 'visible' } : {}),
      wordWrap: 'break-word',
    }),
    ...(includeExpandedMenu
      ? {
          menuList: (provided) => ({
            ...provided,
            maxHeight: 'none',
            paddingBottom: '0',
          })
        }
      : {}),
    option: (provided, state) => {
      const base = {
        ...provided,
        color: state.isDisabled ? 'rgba(32, 32, 32, 0.45)' : 'rgba(32, 32, 32, 1)',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        minHeight: height,
        paddingLeft: '16px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      }
      if (state.isSelected) {
        return {
          ...base,
          background: SELECT_BLUE_SOFT,
          color: '#055a8c',
          fontWeight: 500,
          ':hover': { background: SELECT_BLUE_SOFT_HOVER },
          ':active': { background: SELECT_BLUE_SOFT_HOVER },
        }
      }
      return {
        ...base,
        background: '#fff',
        ':hover': { background: getOptionHoverBackground(state) },
      }
    },
    placeholder: (provided, state) => {
      if (!isMulti) {
        return {
          ...provided,
          marginLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }
      }
      return {
        ...provided,
        marginLeft: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        pointerEvents: 'none',
        color: 'rgba(32, 32, 32, 0.7)',
        zIndex: 0,
      }
    },
    input: (provided) => ({
      ...provided,
      margin: isMulti ? '2px 0 2px 2px' : inputMargin,
      minWidth: isMulti ? '20px' : undefined,
      ...(isMulti ? { position: 'relative', zIndex: 1 } : {}),
    }),
    singleValue: (provided) => ({
      ...provided,
      width: twoStepWidth ? singleValueWidth : singleValueResponsiveWidth,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    valueContainer: (provided, state) => {
      const has = selectHasValue(state)
      return {
        ...provided,
        ...(isMulti
          ? {
              flexWrap: 'nowrap',
              overflow: 'hidden',
              flex: '1 1 0',
              minWidth: 0,
              alignItems: 'center',
              justifyContent: 'flex-start',
              position: 'relative',
              height: '100%',
              paddingRight: has ? '2px' : 0,
            }
          : {}),
      }
    },
    indicatorsContainer: (provided) => ({
      ...provided,
      ...(isMulti ? { flexShrink: 0, alignSelf: 'stretch', alignItems: 'center' } : {}),
    }),
    menuPortal: (base) => ({ ...base, zIndex: zIndex }),
  }
}
